import { useEffect, useState, useCallback } from 'react';
import { Table, Select, Button, Space, Tag, Card, Row, Col, Statistic } from 'antd';
import { CSVLink } from 'react-csv';
import { FileExcelOutlined, EnvironmentOutlined, VideoCameraOutlined, HomeOutlined, ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

const TutorClassLogPayment = ({ onChange = () => {} }) => {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [teacherRates, setTeacherRates] = useState([]);
  const [branchFilter, setBranchFilter] = useState(null);
  const [registrationCounts, setRegistrationCounts] = useState({}); 
  const [approvedHours, setApprovedHours] = useState([]);
  const [adminapproveFilter, setAdminapproveFilter] = useState('');
  const [payoutFilter, setPayoutFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100); // default 100

    const [dateRange, setDateRange] = useState([
      dayjs().startOf('month'),
      dayjs().endOf('month'),
    ]);

  const API_URL = process.env.REACT_APP_API_URL; 

  
  // Updated helper function to be teacher-specific
  const getRateBySubjectAndStudentCount = (teacherId, subjectGroupName, studentCount, allTeacherRates) => {
    if (!allTeacherRates || !Array.isArray(allTeacherRates)) return 0;
    
    // Find rates specific to this teacher
    const teacherSpecificRates = allTeacherRates.filter(rate => rate.teacher_id === teacherId);
    
    if (teacherSpecificRates.length === 0) return 0; // No rates set for this teacher
    
    let classSizeCategory;
    if (studentCount >= 1 && studentCount <= 2) {
      classSizeCategory = 'private_1_2';
    } else if (studentCount >= 3 && studentCount <= 5) {
      classSizeCategory = 'group_3_5';
    } else if (studentCount >= 6 && studentCount <= 10) {
      classSizeCategory = 'group_6_10';
    } else {
      classSizeCategory = 'private_1_2';
    }
    
    const matchingRate = teacherSpecificRates.find(rate => 
      rate.subject_group_name === subjectGroupName && 
      rate.class_size_category === classSizeCategory
    );
    
    return matchingRate ? matchingRate.rate : 0;
  };
useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch branches", err);
    }
  };

  fetchBranches();
}, [API_URL]);
  // Add the fetch registration counts function
  const fetchRegistrationCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/registrations/registrations-count`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const countsObject = {};
      if (Array.isArray(response.data)) {
        response.data.forEach(item => {
          countsObject[item.groupid] = item.registered_students;
        });
      }
      
      setRegistrationCounts(countsObject);
    } catch (error) {
      console.error('Error fetching registration counts:', error);
    }
  }, [API_URL]);

useEffect(() => {
  const fetchClassesAndRates = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const from = dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
      const to   = dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;

      if (!from || !to) return; // ✅ only fetch when both dates are set

      const [classRes, rateRes, approvedRes] = await Promise.all([
        axios.get(`${API_URL}/classes/tutor-class-log-payment`, {
          headers,
          params: {
            startDate: from,
            endDate: to,
          },
        }),
        axios.get(`${API_URL}/teacherrate`, { headers }),
        axios.get(`${API_URL}/teacher-hours/approved/by-range`, {
          headers,
          params: {
            from,
            to,
          },
        }),
      ]);

      setClasses(classRes.data);
      setFilteredClasses(classRes.data);
      setTeacherRates(rateRes.data);
      setApprovedHours(approvedRes.data);

    } catch (error) {
      console.error('Error fetching classes, rates, or approved hours:', error);
    }
  };

  fetchClassesAndRates();
  fetchRegistrationCounts();
}, [fetchRegistrationCounts, API_URL, dateRange]);


useEffect(() => {
  const filtered = classes.filter(cls => {
    const classDate = dayjs(cls.date);

    return (
      (dateRange?.[0] ? classDate.isSameOrAfter(dateRange[0], 'day') : true) &&
      (dateRange?.[1] ? classDate.isSameOrBefore(dateRange[1], 'day') : true) &&
      (statusFilter ? cls.status === statusFilter : true) &&
      (adminapproveFilter ? cls.adminapprove === adminapproveFilter : true) &&
      (payoutFilter ? cls.payout === payoutFilter : true) &&
      (branchFilter ? cls.branch_id === branchFilter : true) // Use branch_id
    );
  });

  setFilteredClasses(filtered);
  setCurrentPage(1);
}, [dateRange, statusFilter, adminapproveFilter, payoutFilter, branchFilter, classes]);


  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return 'N/A';
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    
    let durationHours = 0;
    let durationMinutes = 0;
    
    if (duration.includes('h') || duration.includes('m')) {
      const hoursMatch = duration.match(/(\d+)h/);
      const minutesMatch = duration.match(/(\d+)m/);
      
      durationHours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
      durationMinutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
    } 
    else if (duration.includes(':')) {
      [durationHours, durationMinutes] = duration.split(':').map(Number);
    }
    
    let endHours = startHours + durationHours;
    let endMinutes = startMinutes + durationMinutes;
    
    if (endMinutes >= 60) {
      endHours += Math.floor(endMinutes / 60);
      endMinutes %= 60;
    }
    
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const parseScheduleHourToDecimal = (str) => {
    if (!str) return 0;
    const [hPart, mPart] = str.split(' ');
    const hours = parseInt(hPart.replace('h', '')) || 0;
    const minutes = parseInt(mPart.replace('m', '')) || 0;
    return hours + minutes / 60;
  };

  const handleDateRangeChange = (index, value) => {
  const newRange = [...dateRange];
  newRange[index] = value ? dayjs(value) : null;
  setDateRange(newRange);
};

const getApprovedAmount = (classId) => {
  const record = approvedHours.find(a => a.class_id === classId);
  return record ? record.total_amount : null;
};

const getApprovedRecord = (classId) => {
  return approvedHours.find(a => a.class_id === classId) || null;
};

// Calculate statistics based on filtered classes
const calculateStatistics = () => {
  const stats = {
    totalHours: 0,
    teacherConfirmedHours: 0,
    teacherUnconfirmedHours: 0,
    adminConfirmedHours: 0,
    adminUnconfirmedHours: 0,
    paidHours: 0,
    unpaidHours: 0,
  };

  filteredClasses.forEach(cls => {
    const records = cls.attendance_statuses || [];
    const rescheduled = records.length > 0 && records.every((r) => r.status === 'To be rescheduled');
    if (rescheduled) return; // rescheduled classes don't pay — skip from totals

    const hours = parseScheduleHourToDecimal(cls.schedule_hour);

    // Total hours
    stats.totalHours += hours;

    // Teacher confirmation status
    if (cls.status === 'approved') {
      stats.teacherConfirmedHours += hours;
    } else {
      stats.teacherUnconfirmedHours += hours;
    }

    // Admin confirmation status
    if (cls.adminapprove === 'adminconfirm') {
      stats.adminConfirmedHours += hours;
    } else {
      stats.adminUnconfirmedHours += hours;
    }

    // Payout status
    if (cls.payout === 'paid') {
      stats.paidHours += hours;
    } else {
      stats.unpaidHours += hours;
    }
  });

  return stats;
};

const stats = calculateStatistics();

 // Updated CSV export (with dateRange support)
const csvData = filteredClasses.map(cls => {
  const studentCount = registrationCounts[cls.groupid] || 0;
  const records = cls.attendance_statuses || [];
  const rescheduled = records.length > 0 && records.every((r) => r.status === 'To be rescheduled');
  const rate = rescheduled ? null : getRateBySubjectAndStudentCount(
    cls.teacher_id,
    cls.subject_group_name,
    studentCount,
    teacherRates
  );
  const hours = parseScheduleHourToDecimal(cls.schedule_hour);
  const total = rate !== null && !isNaN(hours) ? rate * hours : null;
  const attendanceSummary = records.map((r) => `${r.student_id}:${r.status}`).join(' | ');

  let timeRange = '';
  if (cls.schedule_time && cls.schedule_hour) {
    const startTime = cls.schedule_time.split(':').slice(0, 2).join(':');
    const endTime = calculateEndTime(startTime, cls.schedule_hour);
    timeRange = `${startTime} - ${endTime}`;
  }

  let classSizeCategory = '';
  if (studentCount >= 1 && studentCount <= 2) {
    classSizeCategory = 'Private (1-2)';
  } else if (studentCount >= 3 && studentCount <= 5) {
    classSizeCategory = 'Group (3-5)';
  } else if (studentCount >= 6 && studentCount <= 10) {
    classSizeCategory = 'Group (6-10)';
  } else if (studentCount > 10) {
    classSizeCategory = 'Oversized (>10)';
  }

  return {
    BranchName: cls.name,
    ClassID: cls.classid,
    Date: dayjs(cls.date).format('DD MMM YYYY'), // ✅ use consistent format
    ClassCode: cls.class_code,
    Subject: cls.subject_name,
    SubjectGroup: cls.subject_group_name,
    Teacher: `(${cls.nickname}) ${cls.teacher_name}`,
    Platform: cls.platform,
    Time: timeRange,
    ScheduleHour: cls.schedule_hour,
    RegisteredStudents: studentCount,
    ClassSizeCategory: classSizeCategory,
    Attendance: attendanceSummary,
    Rate: rescheduled ? 'Rescheduled' : (rate !== null ? rate : 'N/A'),
    Total: rescheduled ? 'Rescheduled' : (total !== null ? total.toFixed(2) : 'N/A'),
    TutorConfirm: cls.status === 'approved' ? 'Approved' : 'Pending',
    AdminConfirm: cls.adminapprove === 'adminconfirm' ? 'Confirmed' : 'Pending',
    Payout: cls.payout === 'pending' ? 'Pending' : 'Paid',
  };
});

// ✅ Dynamic filename based on dateRange
let csvFilename = 'tutor_class_log.csv';
if (dateRange?.[0] && dateRange?.[1]) {
  csvFilename = `tutor_class_log_${dayjs(dateRange[0]).format('YYYY-MM-DD')}_to_${dayjs(dateRange[1]).format('YYYY-MM-DD')}.csv`;
} else if (dateRange?.[0]) {
  csvFilename = `tutor_class_log_from_${dayjs(dateRange[0]).format('YYYY-MM-DD')}.csv`;
} else if (dateRange?.[1]) {
  csvFilename = `tutor_class_log_until_${dayjs(dateRange[1]).format('YYYY-MM-DD')}.csv`;
}

 const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta",
};

  // True when every attendance record on the class is "To be rescheduled".
  const isClassRescheduled = (cls) => {
    const records = cls?.attendance_statuses || [];
    return records.length > 0 && records.every((r) => r.status === 'To be rescheduled');
  };

  const columns = [
    {
            title: t("branch"),
            key: "name",
            dataIndex: "name",
            width: 120,
            render: (_, record) => {
              const color = branchColors[record.branch_id] || "default";
              return (
                <Tag color={color}>
                  {record.name || t("N/A")}
                </Tag>
              );
            },
          },
    {
      title: t('schedule') || 'Schedule',
      key: 'schedule',
      width: 160,
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: (_, record) => {
        const dateStr = new Date(record.date).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short', year: '2-digit',
        });
        const start = record.schedule_time?.split(':').slice(0, 2).join(':');
        const end = start ? calculateEndTime(start, record.schedule_hour) : '';
        return (
          <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>
            <div style={{ fontWeight: 600 }}>{dateStr}</div>
            <div style={{ fontSize: 12, color: '#555' }}>{start} - {end}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{record.schedule_hour}</div>
          </div>
        );
      },
    },
    {
      title: t('class') || 'Class',
      key: 'class_summary',
      width: 320,
      render: (_, record) => (
        <div style={{ whiteSpace: 'normal', overflowWrap: 'break-word', lineHeight: 1.35 }}>
          <div style={{ fontSize: 11, color: '#888' }}>#{record.classid}</div>
          <div style={{ fontWeight: 600 }}>{record.class_code}</div>
          <div style={{ fontSize: 12, color: '#555' }}>{record.subject_name}</div>
        </div>
      ),
    },
    { title: t('Pay group'), dataIndex: 'subject_group_name', key: 'subject_group_name', width: 80 },
    { 
      title: t('teacher'), 
      dataIndex: 'teacher_name', 
      key: 'teacher_name',
      render: (_, cls) => `(${cls.nickname}) ${cls.teacher_name}`
    },
    {
      title: t('platform'),
      dataIndex: 'platform',
      key: 'platform',
      render: (platform) => {
        switch (platform) {
          case 'onsite':
            return (
              <Tag color="green" icon={<EnvironmentOutlined />}>
                Onsite
              </Tag>
            );
          case 'online':
            return (
              <Tag color="blue" icon={<VideoCameraOutlined />}>
                Online
              </Tag>
            );
          case 'athome':
            return (
              <Tag color="orange" icon={<HomeOutlined />}>
                At Home
              </Tag>
            );
          default:
            return <Tag>{platform}</Tag>;
        }
      },
    },
    {
      title: t('students'),
      key: 'registered_students',
      width: 120,
      render: (_, record) => {
        const count = registrationCounts[record.groupid] || 0;

        let color = 'default';
        let categoryText = '';
        
        if (count >= 1 && count <= 2) {
          color = 'purple';
          categoryText = 'Private';
        } else if (count >= 3 && count <= 5) {
          color = 'blue';
          categoryText = 'Small Group';
        } else if (count >= 6 && count <= 10) {
          color = 'green';
          categoryText = 'Large Group';
        } else if (count > 10) {
          color = 'red';
          categoryText = 'Oversized';
        }
        
        return (
          <div>
            <Tag color={color}>
              {count}
            </Tag>
            {categoryText && (
              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                {categoryText}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('attendance') || 'Attendance',
      key: 'attendance',
      width: 160,
      render: (_, record) => {
        const records = record.attendance_statuses || [];
        if (records.length === 0) {
          return <span style={{ color: '#aaa', fontSize: 11 }}>—</span>;
        }
        const counts = records.reduce((acc, r) => {
          acc[r.status] = (acc[r.status] || 0) + 1;
          return acc;
        }, {});
        const badge = (label, count, bg, color, border) => count > 0 && (
          <span
            key={label}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              height: 18, padding: '0 6px', marginRight: 3, marginBottom: 3,
              borderRadius: 9, background: bg, color,
              border: border ? `1px solid ${border}` : 'none',
              fontSize: 10, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap',
            }}
          >
            {label}{count > 1 ? ` ${count}` : ''}
          </span>
        );
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {badge('Present', counts['Present'] || 0, '#52c41a', '#fff')}
            {badge('Absent', counts['Absent'] || 0, '#fff', '#cf1322', '#ff4d4f')}
            {badge('TBR', counts['To be rescheduled'] || 0, '#ff4d4f', '#fff')}
            {badge('Late', counts['Late'] || 0, '#faad14', '#fff')}
          </div>
        );
      },
    },
    {
      title: t('rate'),
      key: 'rate',
      render: (_, record) => {
        if (isClassRescheduled(record)) {
          return <span style={{ color: '#aaa', fontSize: 11 }}>—</span>;
        }
        const studentCount = registrationCounts[record.groupid] || 0;
        const rate = getRateBySubjectAndStudentCount(
          record.teacher_id, // Add teacher_id
          record.subject_group_name,
          studentCount,
          teacherRates // Use teacherRates (the state variable)
        );
        return rate !== null && rate > 0 ? `฿${rate.toLocaleString()}` : 'No Rate Set';
      }
    },
    {
      title: t('total'),
      key: 'total',
      width: 120,
      render: (_, record) => {
        if (isClassRescheduled(record)) {
          return <span style={{ color: '#aaa', fontSize: 11 }}>—</span>;
        }
        const studentCount = registrationCounts[record.groupid] || 0;
        const rate = getRateBySubjectAndStudentCount(
          record.teacher_id, // Add teacher_id
          record.subject_group_name,
          studentCount,
          teacherRates
        );
        const hours = parseScheduleHourToDecimal(record.schedule_hour);
        const total = rate && rate > 0 && !isNaN(hours) ? rate * hours : null;

        return total !== null ? `฿${total.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}` : 'No Rate Set';
      }
    },
    {
  title: t('approved Rate'),
  key: 'approvedRate',
  width: 120,
  render: (_, record) => {
    const approved = getApprovedRecord(record.classid);
    return approved && approved.hourly_rate
      ? `฿${approved.hourly_rate.toLocaleString()}`
      : '-';
  }
},
    {
  title: t('paid'),
  key: 'approvedTotal',
  width: 140,
  render: (_, record) => {
    const approved = getApprovedAmount(record.classid);

    return approved !== null
      ? `฿${approved.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '-'; // or "Pending"
  }
},
    { 
      title: t('Teacher CF'), 
      dataIndex: 'status', 
      key: 'status',
      render: (status) => {
        const isApproved = status === 'approved';
        return (
          <Tag 
            color={isApproved ? '#f6ffed' : '#fffbe6'} 
            style={{ 
              color: isApproved ? '#52c41a' : '#faad14',
              borderColor: isApproved ? '#b7eb8f' : '#ffe58f'
            }}
          >
            {isApproved ? t('Teacher CF') : t('pending')}
          </Tag>
        );
      }
    },
    { 
      title: t('Admin CF'), 
      dataIndex: 'adminapprove', 
      key: 'adminapprove',
      render: (adminapprove) => {
        const isConfirmed = adminapprove === 'adminconfirm';
        return (
          <Tag 
            color={isConfirmed ? '#f6ffed' : '#fffbe6'} 
            style={{ 
              color: isConfirmed ? '#52c41a' : '#faad14',
              borderColor: isConfirmed ? '#b7eb8f' : '#ffe58f'
            }}
          >
            {isConfirmed ? t('Admin CF') : t('pending')}
          </Tag>
        );
      }
    },
    { 
      title: t('payout'), 
      dataIndex: 'payout', 
      key: 'payout',
      render: (payout) => {
        const isPending = payout === 'pending';
        return (
          <Tag 
            color={isPending ? '#fff1f0' : '#f6ffed'} 
            style={{ 
              color: isPending ? '#f5222d' : '#52c41a',
              borderColor: isPending ? '#ffa39e' : '#b7eb8f'
            }}
          >
            {isPending ? t('Pending') : t('Paid')}
          </Tag>
        );
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Statistics Cards */}
      <div style={{ marginBottom: 24 }}>
          {/* Top row: Total Hours + Date Range */}
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={12} lg={8}>
            <Card>
              <Statistic
                title={t('Total Hours')}
                value={stats.totalHours.toFixed(1)}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
              />
              {/* Date Range Display */}
              <div style={{ marginTop: 8, fontSize: '12px', color: '#888' }}>
                {dateRange[0] && dateRange[1] ? (
                  <>
                    {dateRange[0].format('DD MMM YYYY')} - {dateRange[1].format('DD MMM YYYY')}
                  </>
                ) : (
                  <span>{t('No date range selected')}</span>
                )}
              </div>
            </Card>
          </Col>
          </Row>

          {/* Second row: Teacher + Admin */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('Teacher Confirmed (Taught Classes)')}
                  value={stats.teacherConfirmedHours.toFixed(1)}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('Teacher Unconfirmed')}
                  value={stats.teacherUnconfirmedHours.toFixed(1)}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('Admin Confirmed')}
                  value={stats.adminConfirmedHours.toFixed(1)}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('Admin Unconfirmed')}
                  value={stats.adminUnconfirmedHours.toFixed(1)}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Third row: Payout */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('Paid Hours')}
                  value={stats.paidHours.toFixed(1)}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('Unpaid Hours')}
                  value={stats.unpaidHours.toFixed(1)}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
          </Row>
        </div>
      {/* Filters and Controls */}
      <div>
        <Space style={{ margin: 16 }}>
      <div>            
      <Select
          placeholder="Select Branch"
          value={branchFilter}
          onChange={(value) => setBranchFilter(value)}
          allowClear
          style={{ width: 200 }}
        >
          {branches.map((branch) => (
            <Option key={branch.id} value={branch.id}> {/* Use branch.id as value */}
              {branch.name}
            </Option>
          ))}
        </Select>
            </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span>{t('Date')}:</span>

            {/* From Date */}
            <div>
              <input
                type="date"
                id="hidden-from-date"
                value={dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : ''}
                onChange={(e) => handleDateRangeChange(0, e.target.value)}
                style={{ display: 'none' }}
              />
              <input
                type="text"
                readOnly
                value={dateRange[0] ? dateRange[0].format('DD MMM YYYY') : ''}
                placeholder="DD-MMM-YYYY"
                onClick={() => document.getElementById('hidden-from-date')?.showPicker()}
                style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  border: '1px solid #d9d9d9',
                  cursor: 'pointer',
                  width: '140px',
                  textAlign: 'center',
                }}
              />
            </div>

            <span>to</span>

            {/* To Date */}
            <div>
              <input
                type="date"
                id="hidden-to-date"
                value={dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : ''}
                onChange={(e) => handleDateRangeChange(1, e.target.value)}
                min={dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined}
                style={{ display: 'none' }}
              />
              <input
                type="text"
                readOnly
                value={dateRange[1] ? dateRange[1].format('DD MMM YYYY') : ''}
                placeholder="DD-MMM-YYYY"
                onClick={() => document.getElementById('hidden-to-date')?.showPicker()}
                style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  border: '1px solid #d9d9d9',
                  cursor: 'pointer',
                  width: '140px',
                  textAlign: 'center',
                }}
              />
            </div>
          </div>
          <div>

          <label style={{ display: 'block' }}></label>
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
            <Option value="">{t('classstatuses')}</Option>
            <Option value="approved">{t('confirmed')}</Option>
            <Option value="pending">{t('confirmpending')}</Option>
          </Select>
          </div>
          <div>
          <label style={{ display: 'block' }}></label>
          <Select value={adminapproveFilter} onChange={setAdminapproveFilter} style={{ width: 160 }}>
            <Option value="">{t('Admin Approve')}</Option>
            <Option value="adminconfirm">{t('Admin Confirmed')}</Option> 
            <Option value="pending">{t('Pending')}</Option>
          </Select>
          </div>
          <div>
          <label style={{ display: 'block' }}></label>
          <Select value={payoutFilter} onChange={setPayoutFilter} style={{ width: 160 }}>
            <Option value="">{t('payout')}</Option>
            <Option value="pending">{t('Pending')}</Option>
            <Option value="paid">{t('Paid')}</Option>
          </Select>
          </div>
          <CSVLink data={csvData} filename={csvFilename}>
            <Button icon={<FileExcelOutlined />} type="primary">
              {t('exportToCSV')}
            </Button>
          </CSVLink>
        </Space>
        </div>

        <div style={{ overflowX: 'auto', width: '100%' }}>
            <Table
              columns={columns}
              dataSource={filteredClasses}
              rowKey="classid"
              pagination={{
                current: currentPage,
                pageSize: itemsPerPage,
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                  // also update itemsPerPage if you want to persist selection
                  setItemsPerPage(pageSize);
                },
                pageSizeOptions: ['100', '200', '500'],
                showSizeChanger: true
              }}
              onRow={(record) => {
                const records = record.attendance_statuses || [];
                let bg = null;
                if (records.some((r) => r.status === 'Absent')) bg = '#fff1f0';
                else if (records.some((r) => r.status === 'To be rescheduled')) bg = '#fffbe6';
                else if (records.some((r) => r.status === 'Present')) bg = '#f6ffed';
                return bg ? { style: { backgroundColor: bg } } : {};
              }}
              scroll={{ x: '1000px' }}
              size="middle"
              bordered
            />
  </div>
      </div>
  );
};

export default withRoleAccess(TutorClassLogPayment, ['superadmin']);